import { getCallable } from "../lib/callFunction";
import type {
  FeedbackSession,
  IssueProposal,
  VoiceComment,
} from "../types/feedback";
import { createLogger } from "../utils/logger";

const geminiLogger = createLogger({ prefix: "GeminiFeedbackSummary" });
const DEFAULT_MODELS = [
  "gemini-2.0-flash-lite",
  "gemini-2.5-flash-lite-preview-09-2025",
  "gemini-2.0-flash",
];
const DEFAULT_PROVIDER = "none";
const MAX_SUMMARY_WORDS = 10;
const MODEL_ALIASES: Record<string, string> = {
  "gemini-2.0-flash-lite": "gemini-2.0-flash",
};

export interface FeedbackIssueDraft {
  title: string;
  description: string;
  suggestions: string[];
  triageSummary: string;
}

export interface CommentSummaryAndParaphrase {
  summary: string;
  paraphrase: string;
}

export class GeminiFeedbackSummaryService {
  // Cache for aggregation results
  private cachedAggregationFingerprint: string | null = null;
  private cachedProposals: IssueProposal[] | null = null;
  private cachedTitleFingerprint: string | null = null;
  private cachedTitle: string | null = null;

  async generateIssueDraft(
    comment: VoiceComment,
    session: FeedbackSession,
    totalComments: number
  ): Promise<FeedbackIssueDraft> {
    const deterministicDraft = buildDeterministicDraft(
      comment,
      session,
      totalComments
    );

    const provider =
      (import.meta.env.VITE_FEEDBACK_SUMMARY_PROVIDER as string | undefined) ??
      DEFAULT_PROVIDER;

    if (provider !== "gemini") {
      return deterministicDraft;
    }

    const configuredModel =
      (import.meta.env.VITE_FEEDBACK_GEMINI_MODEL as string | undefined) ?? "";

    const models = [configuredModel, ...DEFAULT_MODELS]
      .filter(Boolean)
      .map((model) => normalizeModelName(model));

    const prompt = buildGeminiPrompt(comment, session, totalComments);
    const aiDraft = await this.generateDraftWithGemini(models, prompt);

    if (!aiDraft) {
      return deterministicDraft;
    }

    return aiDraft;
  }

  private async generateDraftWithGemini(
    models: string[],
    prompt: string
  ): Promise<FeedbackIssueDraft | null> {
    const text = await this.callGeminiProxy(models, prompt, 700);
    if (!text) return null;
    return parseGeminiDraft(text);
  }

  /** Generate a max-10-word summary for a single voice comment. */
  async generateCommentSummary(comment: VoiceComment): Promise<string> {
    const deterministicSummary = buildDeterministicSummary(comment);

    const provider =
      (import.meta.env.VITE_FEEDBACK_SUMMARY_PROVIDER as string | undefined) ??
      DEFAULT_PROVIDER;

    if (provider !== "gemini") {
      return deterministicSummary;
    }

    const configuredModel =
      (import.meta.env.VITE_FEEDBACK_GEMINI_MODEL as string | undefined) ?? "";

    const models = [configuredModel, ...DEFAULT_MODELS]
      .filter(Boolean)
      .map((model) => normalizeModelName(model));

    const prompt = buildSummaryPrompt(comment);
    const aiSummary = await this.generateTextWithGemini(models, prompt);

    if (!aiSummary) {
      return deterministicSummary;
    }

    return enforceWordLimit(aiSummary.trim(), MAX_SUMMARY_WORDS);
  }

  /**
   * Generate both a brief summary and a paraphrase for a voice comment
   * in a single LLM call, reducing API usage.
   */
  async generateCommentSummaryAndParaphrase(
    comment: VoiceComment
  ): Promise<CommentSummaryAndParaphrase> {
    const deterministicResult: CommentSummaryAndParaphrase = {
      summary: buildDeterministicSummary(comment),
      paraphrase: buildDeterministicParaphrase(comment),
    };

    const provider =
      (import.meta.env.VITE_FEEDBACK_SUMMARY_PROVIDER as string | undefined) ??
      DEFAULT_PROVIDER;

    if (provider !== "gemini") {
      return deterministicResult;
    }

    const configuredModel =
      (import.meta.env.VITE_FEEDBACK_GEMINI_MODEL as string | undefined) ?? "";

    const models = [configuredModel, ...DEFAULT_MODELS]
      .filter(Boolean)
      .map((model) => normalizeModelName(model));

    const prompt = buildSummaryAndParaphrasePrompt(comment);
    const aiText = await this.generateTextWithGemini(models, prompt);

    if (!aiText) {
      return deterministicResult;
    }

    const parsed = parseSummaryAndParaphraseResponse(aiText);
    if (!parsed) {
      return deterministicResult;
    }

    return {
      summary: enforceWordLimit(parsed.summary.trim(), MAX_SUMMARY_WORDS),
      paraphrase: parsed.paraphrase.trim(),
    };
  }

  /** Aggregate all session comments into optimal GitHub issue proposals. */
  async aggregateSessionIssues(
    session: FeedbackSession
  ): Promise<IssueProposal[]> {
    const { comments } = session;
    if (comments.length === 0) return [];

    // Check cache
    const fingerprint = computeCommentsFingerprint(comments);
    if (
      this.cachedAggregationFingerprint === fingerprint &&
      this.cachedProposals
    ) {
      return this.cachedProposals;
    }

    const deterministicProposals = buildDeterministicProposals(
      comments,
      session
    );

    const provider =
      (import.meta.env.VITE_FEEDBACK_SUMMARY_PROVIDER as string | undefined) ??
      DEFAULT_PROVIDER;

    if (provider !== "gemini") {
      this.cachedAggregationFingerprint = fingerprint;
      this.cachedProposals = deterministicProposals;
      return deterministicProposals;
    }

    const configuredModel =
      (import.meta.env.VITE_FEEDBACK_GEMINI_MODEL as string | undefined) ?? "";

    const models = [configuredModel, ...DEFAULT_MODELS]
      .filter(Boolean)
      .map((model) => normalizeModelName(model));

    const prompt = buildAggregationPrompt(comments, session);
    const aiText = await this.generateTextWithGemini(models, prompt);

    if (!aiText) {
      this.cachedAggregationFingerprint = fingerprint;
      this.cachedProposals = deterministicProposals;
      return deterministicProposals;
    }

    const parsed = parseAggregationResponse(aiText, comments);
    if (!parsed) {
      this.cachedAggregationFingerprint = fingerprint;
      this.cachedProposals = deterministicProposals;
      return deterministicProposals;
    }

    this.cachedAggregationFingerprint = fingerprint;
    this.cachedProposals = parsed;
    return parsed;
  }

  /** Generate a concise session title during the feedback preparation stage. */
  async generateSessionTitle(session: FeedbackSession): Promise<string> {
    const { comments } = session;
    const fingerprint = computeCommentsFingerprint(comments);
    if (this.cachedTitleFingerprint === fingerprint && this.cachedTitle) {
      return this.cachedTitle;
    }

    const deterministicTitle = buildDeterministicSessionTitle(session);

    const provider =
      (import.meta.env.VITE_FEEDBACK_SUMMARY_PROVIDER as string | undefined) ??
      DEFAULT_PROVIDER;

    if (provider !== "gemini") {
      this.cachedTitleFingerprint = fingerprint;
      this.cachedTitle = deterministicTitle;
      return deterministicTitle;
    }

    const configuredModel =
      (import.meta.env.VITE_FEEDBACK_GEMINI_MODEL as string | undefined) ?? "";

    const models = [configuredModel, ...DEFAULT_MODELS]
      .filter(Boolean)
      .map((model) => normalizeModelName(model));

    const prompt = buildSessionTitlePrompt(session);
    const aiTitle = await this.generateTextWithGemini(models, prompt);
    if (!aiTitle) {
      this.cachedTitleFingerprint = fingerprint;
      this.cachedTitle = deterministicTitle;
      return deterministicTitle;
    }

    const normalized = normalizeSessionTitle(aiTitle);
    const result = normalized || deterministicTitle;
    this.cachedTitleFingerprint = fingerprint;
    this.cachedTitle = result;
    return result;
  }

  private async generateTextWithGemini(
    models: string[],
    prompt: string
  ): Promise<string | null> {
    return this.callGeminiProxy(models, prompt, 1500);
  }

  /**
   * Call the server-side Gemini proxy Cloud Function.
   * The function handles API key injection and model fallback.
   */
  private async callGeminiProxy(
    models: string[],
    prompt: string,
    maxOutputTokens: number
  ): Promise<string | null> {
    try {
      const callable = getCallable<
        {
          models: string[];
          contents: Array<{ role: string; parts: Array<{ text: string }> }>;
          generationConfig: {
            temperature: number;
            topP: number;
            maxOutputTokens: number;
          };
        },
        { text: string | null }
      >("feedbackGeminiProxy");

      const result = await callable({
        models,
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.15,
          topP: 0.9,
          maxOutputTokens,
        },
      });

      return result.data.text;
    } catch (error) {
      geminiLogger.warn("Gemini proxy call failed", { error });
      return null;
    }
  }
}

function normalizeModelName(model: string): string {
  return MODEL_ALIASES[model] ?? model;
}

function buildGeminiPrompt(
  comment: VoiceComment,
  session: FeedbackSession,
  totalComments: number
): string {
  const screenInsights = comment.screenCaptures.map((screen, index) => {
    const extracted = extractScreenSignals(screen.htmlSnapshot);
    return [
      `Screen ${index + 1}`,
      `- route: ${screen.route}`,
      `- page title: ${screen.pageTitle}`,
      `- viewport: ${screen.viewport.width}x${screen.viewport.height}`,
      `- headings: ${extracted.headings.join(" | ") || "none"}`,
      `- controls: ${extracted.controls.join(" | ") || "none"}`,
      `- key text: ${extracted.keyText.join(" | ") || "none"}`,
      `- semantic ids: ${extracted.semanticIds.join(" | ") || "none"}`,
    ].join("\n");
  });

  return [
    "You are an engineering triage assistant.",
    "Generate structured issue drafting data from user feedback.",
    "Do not use markdown headings with octothorpe.",
    "Do not mention PR references.",
    "Return JSON only.",
    "Keep description under 900 characters.",
    "Keep title under 100 characters.",
    "Suggestions must be at most 3 bullets.",
    "",
    "Required JSON schema:",
    '{"title":"...","description":"...","suggestions":["..."],"triageSummary":"..."}',
    "",
    `Session: ${session.sessionName}`,
    `Comment: ${comment.index} of ${totalComments}`,
    `Duration seconds: ${comment.durationSeconds}`,
    `Transcription: ${comment.transcription || "No transcription provided"}`,
    "",
    "Screen context:",
    ...screenInsights,
    "",
    "description should combine user problem + observed screen state + likely affected areas + repro clues.",
  ].join("\n");
}

function buildDeterministicDraft(
  comment: VoiceComment,
  _session: FeedbackSession,
  totalComments: number
): FeedbackIssueDraft {
  const routes = Array.from(
    new Set(comment.screenCaptures.map((s) => s.route))
  );
  const firstScreen = comment.screenCaptures[0];
  const firstSignals = firstScreen
    ? extractScreenSignals(firstScreen.htmlSnapshot)
    : {
        headings: [] as string[],
        controls: [] as string[],
        keyText: [] as string[],
        semanticIds: [] as string[],
      };

  const triageSummary = [
    "User problem",
    `- ${truncate(comment.transcription || "No transcription captured", 220)}`,
    "",
    "What is visible on screen",
    `- Routes: ${routes.join(", ") || "none"}`,
    `- Primary page: ${firstScreen?.pageTitle || "unknown"}`,
    `- Notable headings: ${firstSignals.headings.join(" | ") || "none"}`,
    `- Notable controls: ${firstSignals.controls.join(" | ") || "none"}`,
    "",
    "Likely affected areas",
    `- Semantic ids: ${firstSignals.semanticIds.join(" | ") || "none"}`,
    "",
    "Reproduction clues",
    `- Comment ${comment.index} of ${totalComments}; recording duration ${comment.durationSeconds}s`,
  ].join("\n");

  const fallbackTitle = truncate(
    `${firstScreen?.pageTitle || "Feedback"} - ${routes[0] || "unknown-route"}`,
    100
  );

  const description = truncate(
    [
      comment.transcription || "No transcription captured.",
      `Routes visited: ${routes.join(", ") || "none"}.`,
      `Primary page: ${firstScreen?.pageTitle || "unknown"}.`,
      `Notable controls: ${firstSignals.controls.join(" | ") || "none"}.`,
    ].join(" "),
    900
  );

  const suggestions = [
    "Verify the route and component IDs listed in screen context comments.",
    "Check event handlers or state transitions around the primary controls observed.",
    "Reproduce using the same viewport and route sequence captured in this report.",
  ];

  return {
    title: fallbackTitle,
    description,
    suggestions,
    triageSummary,
  };
}

function extractScreenSignals(htmlSnapshot: string): {
  headings: string[];
  controls: string[];
  keyText: string[];
  semanticIds: string[];
} {
  if (!htmlSnapshot) {
    return { headings: [], controls: [], keyText: [], semanticIds: [] };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlSnapshot, "text/html");

  const headings = collectUniqueText(doc, "h1, h2, h3", 8, 90);
  const controls = collectUniqueText(
    doc,
    "button, [role='button'], a[href], input[aria-label], textarea[aria-label]",
    10,
    80
  );

  const keyText = collectUniqueText(
    doc,
    ".error, .warning, .alert, .recommendation-title, .section-heading, .score-status",
    10,
    90
  );

  const semanticIds = Array.from(doc.querySelectorAll("[id]"))
    .map((element) => element.getAttribute("id") || "")
    .filter((id) => id.length > 0)
    .slice(0, 20);

  return {
    headings,
    controls,
    keyText,
    semanticIds,
  };
}

function collectUniqueText(
  doc: Document,
  selector: string,
  maxItems: number,
  maxLengthPerItem: number
): string[] {
  const seen = new Set<string>();
  const values: string[] = [];

  for (const element of Array.from(doc.querySelectorAll(selector))) {
    const raw =
      (element as HTMLElement).innerText ||
      element.getAttribute("aria-label") ||
      "";
    const normalized = raw.replace(/\s+/g, " ").trim();
    if (!normalized) {
      continue;
    }
    const trimmed = truncate(normalized, maxLengthPerItem);
    if (seen.has(trimmed)) {
      continue;
    }

    seen.add(trimmed);
    values.push(trimmed);
    if (values.length >= maxItems) {
      break;
    }
  }

  return values;
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1)}…`;
}

function parseGeminiDraft(rawText: string): FeedbackIssueDraft | null {
  const jsonText = extractJsonObject(rawText);
  if (!jsonText) {
    return null;
  }

  try {
    const parsed = JSON.parse(jsonText) as {
      title?: string;
      description?: string;
      suggestions?: string[];
      triageSummary?: string;
    };

    const title = truncate((parsed.title || "").trim(), 100);
    const description = truncate((parsed.description || "").trim(), 900);
    const triageSummary = truncate((parsed.triageSummary || "").trim(), 1200);
    const suggestions = (parsed.suggestions || [])
      .map((item) => truncate(String(item).trim(), 220))
      .filter(Boolean)
      .slice(0, 3);

    if (!title || !description) {
      return null;
    }

    return {
      title,
      description,
      suggestions,
      triageSummary: triageSummary || description,
    };
  } catch (error) {
    geminiLogger.warn("Failed to parse Gemini draft response", { error });
    return null;
  }
}

function extractJsonObject(text: string): string | null {
  const fencedMatch = text.match(/```json\s*([\s\S]*?)```/i);
  if (fencedMatch?.[1]) {
    return fencedMatch[1].trim();
  }

  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    return null;
  }

  return text.slice(firstBrace, lastBrace + 1).trim();
}

// ---------------------------------------------------------------------------
// Comment summary helpers
// ---------------------------------------------------------------------------

function buildDeterministicSummary(comment: VoiceComment): string {
  const text = comment.transcription.trim();
  if (!text) return "No transcription";
  return enforceWordLimit(text, MAX_SUMMARY_WORDS);
}

function enforceWordLimit(text: string, maxWords: number): string {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return words.join(" ");
  return words.slice(0, maxWords).join(" ") + "…";
}

function buildSummaryPrompt(comment: VoiceComment): string {
  return [
    "Summarize the following voice feedback in at most 10 words.",
    "Return ONLY the summary text, no JSON, no markdown.",
    "",
    `Transcription: ${comment.transcription || "No transcription"}`,
  ].join("\n");
}

function buildDeterministicSessionTitle(session: FeedbackSession): string {
  const routeSet = new Set<string>();
  for (const comment of session.comments) {
    for (const capture of comment.screenCaptures) {
      if (capture.route) {
        routeSet.add(capture.route);
      }
    }
  }

  const routeList = Array.from(routeSet).slice(0, 2);
  if (routeList.length === 0) {
    return "Prepared feedback session";
  }

  return `Feedback for ${routeList.join(" + ")}`;
}

function buildSessionTitlePrompt(session: FeedbackSession): string {
  const commentLines = session.comments.slice(0, 6).map((comment) => {
    const route = comment.screenCaptures[0]?.route ?? "unknown-route";
    const summary = comment.summary || comment.transcription || "No summary";
    return `- [${route}] ${summary}`;
  });

  return [
    "Create a concise session title for grouped product feedback.",
    "Constraints:",
    "- Maximum 8 words",
    "- Plain text only",
    "- No punctuation except / and -",
    "- Focus on primary user problem area",
    "- No markdown",
    "",
    `Current session name: ${session.sessionName}`,
    "Feedback snippets:",
    ...commentLines,
  ].join("\n");
}

function normalizeSessionTitle(raw: string): string {
  const firstLine = raw.split(/\r?\n/).find((line) => line.trim().length > 0);
  if (!firstLine) return "";

  const unquoted = firstLine
    .replace(/^['"`]+/, "")
    .replace(/['"`]+$/, "")
    .trim();

  const words = unquoted.split(/\s+/).filter(Boolean).slice(0, 8);
  if (words.length === 0) return "";

  return words.join(" ").slice(0, 80);
}

// ---------------------------------------------------------------------------
// Session aggregation helpers
// ---------------------------------------------------------------------------

function buildDeterministicProposals(
  comments: VoiceComment[],
  session: FeedbackSession
): IssueProposal[] {
  return comments.map((comment) => {
    const draft = buildDeterministicDraft(comment, session, comments.length);
    return {
      title: draft.title,
      description: draft.description,
      suggestions: draft.suggestions,
      triageSummary: draft.triageSummary,
      labels: ["voice-feedback"],
      category: inferCategory(
        comment.transcription
      ) as IssueProposal["category"],
      sourceCommentIndices: [comment.index],
    };
  });
}

function buildAggregationPrompt(
  comments: VoiceComment[],
  session: FeedbackSession
): string {
  const commentSummaries = comments.map((c) => {
    const routes = Array.from(
      new Set(c.screenCaptures.map((s) => s.route))
    ).join(", ");
    const content =
      c.paraphrase || c.summary || c.transcription || "No content";
    return [
      `Comment ${c.index}:`,
      `  Summary: ${content}`,
      `  Routes: ${routes || "none"}`,
      `  Duration: ${c.durationSeconds}s`,
    ].join("\n");
  });

  return [
    "You are an engineering triage assistant for a software project.",
    "Given the following voice feedback comments from a single session,",
    "determine the optimal number of GitHub issues to create.",
    "Related problems that should be fixed in the same PR should be",
    "aggregated under a single issue.",
    "",
    "Do not use markdown headings with octothorpe in descriptions.",
    "Paraphrase user feedback — do NOT copy transcriptions verbatim.",
    "Return JSON only.",
    "",
    "Required JSON schema:",
    '{"issues":[{"title":"...","description":"...","suggestions":["..."],"triageSummary":"...","labels":["voice-feedback"],"category":"bug|kudos|feature-request|docs-bug|usability|performance","sourceCommentIndices":[1,2]}]}',
    "",
    "Rules:",
    "- Every comment index must appear in at least one issue.",
    "- title: max 100 characters",
    "- description: max 900 characters, paraphrased (not verbatim)",
    "- suggestions: max 3 items",
    "- labels must include 'voice-feedback'",
    "- category: one of bug, kudos, feature-request, docs-bug, usability, performance",
    "",
    `Session: ${session.sessionName}`,
    `Total comments: ${comments.length}`,
    "",
    ...commentSummaries,
  ].join("\n");
}

interface AggregationResponse {
  issues: Array<{
    title?: string;
    description?: string;
    suggestions?: string[];
    triageSummary?: string;
    labels?: string[];
    category?: string;
    sourceCommentIndices?: number[];
  }>;
}

function parseAggregationResponse(
  rawText: string,
  comments: VoiceComment[]
): IssueProposal[] | null {
  const jsonText = extractJsonObject(rawText);
  if (!jsonText) return null;

  try {
    const parsed = JSON.parse(jsonText) as AggregationResponse;
    if (!Array.isArray(parsed.issues) || parsed.issues.length === 0) {
      return null;
    }

    const proposals: IssueProposal[] = parsed.issues
      .filter((issue) => issue.title && issue.description)
      .map((issue) => ({
        title: truncate((issue.title ?? "").trim(), 100),
        description: truncate((issue.description ?? "").trim(), 900),
        suggestions: (issue.suggestions ?? [])
          .map((s) => truncate(String(s).trim(), 220))
          .filter(Boolean)
          .slice(0, 3),
        triageSummary: truncate(
          (issue.triageSummary ?? issue.description ?? "").trim(),
          1200
        ),
        labels: Array.isArray(issue.labels)
          ? issue.labels.filter((l): l is string => typeof l === "string")
          : ["voice-feedback"],
        category: (isValidCategory(issue.category)
          ? issue.category
          : "bug") as IssueProposal["category"],
        sourceCommentIndices: Array.isArray(issue.sourceCommentIndices)
          ? issue.sourceCommentIndices.filter(
              (i): i is number => typeof i === "number"
            )
          : [],
      }));

    if (proposals.length === 0) return null;

    // Ensure every comment is covered
    const coveredIndices = new Set(
      proposals.flatMap((p) => p.sourceCommentIndices)
    );
    for (const comment of comments) {
      if (!coveredIndices.has(comment.index)) {
        proposals.push({
          title: truncate(
            comment.summary ||
              comment.title ||
              `Feedback comment ${comment.index}`,
            100
          ),
          description: truncate(
            comment.transcription || "No transcription available.",
            900
          ),
          suggestions: [],
          triageSummary: comment.summary || comment.transcription || "",
          labels: ["voice-feedback"],
          category: inferCategory(
            comment.transcription
          ) as IssueProposal["category"],
          sourceCommentIndices: [comment.index],
        });
      }
    }

    // Ensure labels always include voice-feedback
    for (const proposal of proposals) {
      if (!proposal.labels.includes("voice-feedback")) {
        proposal.labels.unshift("voice-feedback");
      }
    }

    return proposals;
  } catch (error) {
    geminiLogger.warn("Failed to parse aggregation response", { error });
    return null;
  }
}

const VALID_CATEGORIES = new Set([
  "bug",
  "kudos",
  "feature-request",
  "docs-bug",
  "usability",
  "performance",
]);

function isValidCategory(value: unknown): boolean {
  return typeof value === "string" && VALID_CATEGORIES.has(value);
}

/**
 * Infer a feedback category from transcription text using keyword heuristics.
 * Falls back to "bug" when no strong signal is detected.
 */
function inferCategory(transcription: string): string {
  const lower = (transcription || "").toLowerCase();

  // Kudos / positive
  if (
    /\b(love|great|awesome|amazing|excellent|perfect|thank|nice|good job|well done|kudos|fantastic)\b/.test(
      lower
    )
  ) {
    return "kudos";
  }

  // Feature request
  if (
    /\b(wish|want|would be nice|feature|add|could you|should have|suggestion|request|idea|missing)\b/.test(
      lower
    )
  ) {
    return "feature-request";
  }

  // Documentation
  if (
    /\b(doc|documentation|help text|instructions|guide|tutorial|readme|unclear|confusing text)\b/.test(
      lower
    )
  ) {
    return "docs-bug";
  }

  // Performance
  if (
    /\b(slow|performance|lag|loading|speed|timeout|freeze|unresponsive|takes too long|hang)\b/.test(
      lower
    )
  ) {
    return "performance";
  }

  // Usability
  if (
    /\b(hard to|difficult|confusing|unclear|can't find|where is|navigation|layout|ux|usability|intuitive)\b/.test(
      lower
    )
  ) {
    return "usability";
  }

  // Default: bug
  return "bug";
}

// ---------------------------------------------------------------------------
// Combined summary + paraphrase helpers
// ---------------------------------------------------------------------------

function buildSummaryAndParaphrasePrompt(comment: VoiceComment): string {
  return [
    "Given the following voice feedback transcription, produce two outputs:",
    "1. A summary of at most 10 words.",
    "2. A clear paraphrase that restructures the transcription in professional language.",
    "",
    "Return JSON only. Required schema:",
    '{"summary":"...","paraphrase":"..."}',
    "",
    `Transcription: ${comment.transcription || "No transcription"}`,
  ].join("\n");
}

function parseSummaryAndParaphraseResponse(
  rawText: string
): CommentSummaryAndParaphrase | null {
  const jsonText = extractJsonObject(rawText);
  if (!jsonText) return null;

  try {
    const parsed = JSON.parse(jsonText) as {
      summary?: string;
      paraphrase?: string;
    };
    if (!parsed.summary || !parsed.paraphrase) return null;
    return {
      summary: parsed.summary,
      paraphrase: parsed.paraphrase,
    };
  } catch {
    return null;
  }
}

function buildDeterministicParaphrase(comment: VoiceComment): string {
  const text = comment.transcription.trim();
  if (!text) return "No transcription";
  return text;
}

function computeCommentsFingerprint(comments: VoiceComment[]): string {
  return comments
    .map(
      (c) =>
        `${c.index}:${c.transcription ?? ""}:${c.summary ?? ""}:${c.paraphrase ?? ""}`
    )
    .join("|");
}
